import { CloseScrollStrategy, Overlay } from '@angular/cdk/overlay';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { DatePipe, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import de from '@angular/common/locales/de';
import en from '@angular/common/locales/en';
import fr from '@angular/common/locales/fr';
import nl from '@angular/common/locales/nl';
import pl from '@angular/common/locales/pl';
import {
	importProvidersFrom,
	inject,
	LOCALE_ID,
	provideAppInitializer,
	provideZoneChangeDetection,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY } from '@angular/material/autocomplete';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_TOOLTIP_SCROLL_STRATEGY } from '@angular/material/tooltip';
import { bootstrapApplication } from '@angular/platform-browser';
import '@angular/localize/init';
import 'zone.js';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withRouterConfig } from '@angular/router';
import { ENVIRONMENT_CONTRACT_ASSETS } from '@visiativ/contract-assets-front';
import {
	AuthBffService,
	ENVIRONMENT_ACCOUNT,
	SUPPORT_CASE_HANDLER,
} from '@visiativ/digital-account-front';
import { ENVIRONMENT_APPLICATION_SWITCHER } from '@visiativ/digital-application-switcher-front';
import { ENVIRONMENT_BOOKMARK } from '@visiativ/digital-bookmark-front';
import {
	ENVIRONMENT_DOWNLOAD_CENTER,
	SUPPORT_CASE_HANDLER_DOWNLOAD_CENTER,
} from '@visiativ/digital-download-center-front';
import { ENVIRONMENT, HelpdeskCreateCaseService } from '@visiativ/digital-helpdesk-front';
import { ENVIRONMENT_PROJECT } from '@visiativ/digital-project-front';
import { ENVIRONMENT_NOTIFICATION } from '@visiativ/notification-center-front';
import { ENVIRONMENT_TRAINING_CENTER } from '@visiativ/training-center-front';
import {
	provideUikCore,
	provideUikFonts,
	UikSubNavigationService,
	withMaterialSymbols,
} from '@visiativ/uik-am';
import { OAuthModule } from 'angular-oauth2-oidc';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { AppComponent } from './app/app.component';
import { routes, routesConfig } from './app/app.routes';
import { authErrorInterceptor } from './app/core/interceptors/auth-error.interceptor';
import { credentialsInterceptor } from './app/core/interceptors/credentials.interceptor';
import { AppSubNavigationService } from './app/core/services/app-sub-navigation.service';
import { MyPaginatorIntl } from './app/core/utils/utils';
import { environment } from './environments/environment';

function scrollFactory(overlay: Overlay): () => CloseScrollStrategy {
	return () => overlay.scrollStrategies.close();
}

const INVITE_PATH_RE = /^\/((?:fr|en|de|nl|pl)\/)?invite(\/|$)/;

async function initApp() {
	if (INVITE_PATH_RE.test(window.location.pathname)) {
		return Promise.resolve();
	}
	const auth = inject(AuthBffService);

	const uikNavigationService = inject(UikSubNavigationService);
	const appSubNavigationService = inject(AppSubNavigationService);

	await auth.init();

	uikNavigationService.configure('helpdesk', () =>
		appSubNavigationService.configureHelpdeskComponentsSubNavigation(),
	);
	uikNavigationService.configure('company', () =>
		appSubNavigationService.configureCompanyComponentsSubNavigation(),
	);
	uikNavigationService.configure('training', () =>
		appSubNavigationService.configureTrainingComponentsSubNavigation(),
	);
}
function getAndSetLocale() {
	const currentLang = $localize.locale ?? 'fr';
	switch (currentLang) {
		case 'de':
			registerLocaleData(de);
			break;
		case 'en':
			registerLocaleData(en);
			break;
		case 'nl':
			registerLocaleData(nl);
			break;
		case 'pl':
			registerLocaleData(pl);
			break;
		default:
			registerLocaleData(fr);
	}
	return currentLang;
}
bootstrapApplication(AppComponent, {
	providers: [
		provideUikCore(),
		provideUikFonts(withMaterialSymbols()),
		provideZoneChangeDetection(),
		provideRouter(routes, withRouterConfig(routesConfig)),
		{
			provide: LOCALE_ID,
			useFactory: () => {
				return getAndSetLocale();
			},
		},
		{
			provide: MAT_DATE_LOCALE,
			useFactory: () => {
				return getAndSetLocale();
			},
		},
		{ provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY, useFactory: scrollFactory, deps: [Overlay] },
		{ provide: ENVIRONMENT, useValue: environment },
		{ provide: ENVIRONMENT_PROJECT, useValue: environment },
		{ provide: ENVIRONMENT_NOTIFICATION, useValue: environment },
		{ provide: ENVIRONMENT_CONTRACT_ASSETS, useValue: environment },
		{ provide: ENVIRONMENT_TRAINING_CENTER, useValue: environment },
		{ provide: ENVIRONMENT_ACCOUNT, useValue: environment },
		{ provide: ENVIRONMENT_APPLICATION_SWITCHER, useValue: environment },
		{ provide: ENVIRONMENT_BOOKMARK, useValue: environment },
		{ provide: SUPPORT_CASE_HANDLER, useExisting: HelpdeskCreateCaseService },
		{ provide: ENVIRONMENT_DOWNLOAD_CENTER, useValue: environment },
		{ provide: SUPPORT_CASE_HANDLER_DOWNLOAD_CENTER, useExisting: HelpdeskCreateCaseService },
		FormBuilder,
		{ provide: MAT_TOOLTIP_SCROLL_STRATEGY, useFactory: scrollFactory, deps: [Overlay] },
		{ provide: MatPaginatorIntl, useValue: MyPaginatorIntl() },
		{
			provide: STEPPER_GLOBAL_OPTIONS,
			useValue: { displayDefaultIndicatorType: false },
		},
		provideHttpClient(withInterceptors([credentialsInterceptor, authErrorInterceptor])),
		provideAppInitializer(initApp),
		DatePipe,
		importProvidersFrom(
			OAuthModule.forRoot({
				resourceServer: {
					sendAccessToken: true,
					allowedUrls: [environment.gatewayUrl],
				},
			}),
			LoggerModule.forRoot({
				level: NgxLoggerLevel.LOG, // Use LOG level to test, then, to avoid too many messages in browser console, set it to ERROR
				// serverLogLevel: NgxLoggerLevel.LOG
				// serverLoggingUrl: '/api/logs',
			}),
		),
		provideAnimations(),
	],
}).catch(err => console.error(err));
